import React, { useEffect, useState, memo, useRef } from 'react';
import { View, Text } from '@adobe/react-spectrum';
import { FormField } from '../../store/slice';
import { API_ENDPOINTS, fetchDropdownOptions } from '../../store/api';
import { getCookie } from '../../../../../utils/helpers';
import styles from '../FormField.module.scss';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';

interface OptionItem {
  value: string;
  label: string;
}

interface MultiSelectDropdownComponentProps {
  field: FormField;
  value: string[];
  onChange: (name: string, value: string[]) => void;
  formValues: Record<string, any>;
  showCheckbox?: boolean;
  onFocus?: () => void;
}

/**
 * Component for rendering multi-select dropdown fields
 */
export const MultiSelectDropdownComponent: React.FC<MultiSelectDropdownComponentProps> =
  memo(
    ({
      field,
      value = [],
      onChange,
      formValues,
      showCheckbox = true,
      onFocus
    }) => {
      const [options, setOptions] = useState<OptionItem[]>([]);
      const [isLoading, setIsLoading] = useState<boolean>(false);
      const [error, setError] = useState<string | null>(null);
      const [isOpen, setIsOpen] = useState(false);
      const dropdownRef = useRef<HTMLDivElement>(null);

      // Close dropdown when clicking outside
      useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target as Node)
          ) {
            setIsOpen(false);
          }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, []);

      // Fetch options from API if needed
      useEffect(() => {
        const fetchOptionsFromApi = async () => {
          if (!field.api) return;

          setIsLoading(true);
          setError(null);

          try {
            const options = await fetchDropdownOptions(
              field.api,
              field.dependsOn
                ? { [field.dependsOn]: formValues[field.dependsOn] }
                : undefined
            );

            setOptions(
              options.map(item => ({
                value: item.id || item.value || '',
                label: item.name || item.label || ''
              }))
            );
          } catch (error) {
            setError(
              `Failed to load options: ${
                error instanceof Error ? error.message : 'Unknown error'
              }`
            );
          } finally {
            setIsLoading(false);
          }
        };

        fetchOptionsFromApi();
      }, [field.api, field.dependsOn, formValues]);

      // If we have predefined options, use those instead of API
      useEffect(() => {
        if (field.options) {
          setOptions(field.options);
        }
      }, [field.options]);

      // Toggle an option's selection
      const toggleOption = (optionValue: string) => {
        const newValue = [...value];
        const index = newValue.indexOf(optionValue);

        if (index === -1) {
          newValue.push(optionValue);
        } else {
          newValue.splice(index, 1);
        }

        onChange(field.name, newValue);
      };

      // Get display text for selected options
      const getSelectedText = () => {
        if (value.length === 0) {
          return field.placeholder || 'Select options';
        }

        if (value.length === 1) {
          const selectedOption = options.find(opt => opt.value === value[0]);
          return selectedOption ? selectedOption.label : '1 selected';
        }

        return `${value.length} selected`;
      };

      return (
        <View>
          <label className={styles.label}>
            {field.label}
            {field.required && <span className={styles.required}> *</span>}
          </label>

          <div className={styles.customDropdown} ref={dropdownRef}>
            <button
              type="button"
              className={styles.dropdownButton}
              onClick={() => setIsOpen(!isOpen)}
              onFocus={onFocus}
              disabled={field.disabled || isLoading || options.length === 0}
            >
              <span>{getSelectedText()}</span>
              <ChevronDown />
            </button>

            {isOpen && (
              <div className={styles.dropdownList}>
                {options.map(option => (
                  <div
                    key={option.value}
                    className={`${styles.dropdownItem} ${
                      value.includes(option.value) ? styles.selected : ''
                    }`}
                    onClick={() => toggleOption(option.value)}
                  >
                    {showCheckbox ? (
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={value.includes(option.value)}
                          onChange={() => toggleOption(option.value)}
                          disabled={field.disabled || isLoading}
                        />
                        <span>{option.label}</span>
                      </label>
                    ) : (
                      <div className={styles.optionLabel}>
                        <span>{option.label}</span>
                        {value.includes(option.value) && (
                          <BlackCheckMarkIcon
                            className={styles.checkmarkIcon}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {options.length === 0 && !isLoading && (
                  <div className={styles.dropdownItem}>
                    No options available
                  </div>
                )}
              </div>
            )}
          </div>

          {isLoading && <Text>Loading options...</Text>}
          {error && <Text UNSAFE_className={styles.errorText}>{error}</Text>}
        </View>
      );
    }
  );
